'use client';
import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  IconButton,
} from '@mui/material';
import { faTimes } from '@fortawesome/pro-regular-svg-icons';
import RenderFormSection from 'src/components/era-package/forms/RenderFormSection';
import Button from 'src/components/era-package/forms/Button';
import Buttons from 'src/components/era-package/era/Buttons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

const ResponseModal = ({
  title = 'Details',
  open,
  onClose,
  responseData,
  fields,
  optionsMap = {},
}) => {
  const [formData, setFormData] = useState({});
  const dialogRef = useRef(null);

  // Initialize form data when modal opens or responseData changes
  useEffect(() => {
    if (responseData && fields) {
      // Map responseData to form structure
      const mappedData = {};
      fields.forEach(field => {
        mappedData[field.name] = responseData[field.name] || field.defaultValue || '';
      });
      setFormData(mappedData);
    }
  }, [responseData, fields, open]);

  // Handle keyboard navigation (consistent with EditModal)
  const handleKeyDown = (event) => {
    if (event.key === 'Escape') {
      onClose();
    } else if (event.key === 'Tab') {
      // Ensure focus stays within the modal
      const dialog = dialogRef.current;
      if (dialog) {
        const focusableElements = dialog.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];

        if (event.shiftKey) {
          // Shift + Tab
          if (document.activeElement === firstElement) {
            event.preventDefault();
            lastElement.focus();
          }
        } else {
          // Tab
          if (document.activeElement === lastElement) {
            event.preventDefault();
            firstElement.focus();
          }
        }
      }
    }
  };

  // Don't render if no data
  if (!responseData || !fields) return null;

  const formSection = {
    title: 'Details',
    fields: fields,
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      ref={dialogRef}
      PaperProps={{
        sx: {
          maxHeight: '90vh',
        },
      }}
      // Same focus management as EditModal
      disableEnforceFocus={false}
      disableAutoFocus={false}
      disableRestoreFocus={false}
      onKeyDown={handleKeyDown}
      aria-labelledby="response-modal-title"
      aria-describedby="response-modal-description"
      role="dialog"
    >
      <DialogTitle
        id="response-modal-title"
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
      >
        {title}
        <IconButton
          aria-label={`Close ${title.toLowerCase()} modal`}
          onClick={onClose}
          sx={{
            color: (theme) => theme.palette.grey[500],
          }}
          title="Close modal"
        >
          <Box component={FontAwesomeIcon} icon={faTimes} sx={{ width: '1em' }} />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers id="response-modal-description">
        <Box sx={{ py: 2 }}>
          <RenderFormSection
            section={formSection}
            optionsMap={optionsMap}
            formData={formData}
            forceFormData
            setFormData={() => { }} // Read-only, no updates needed
            errors={{}} // No validation errors in read-only mode
            setErrors={() => { }} // No error handling needed
            setHasUnsaved={() => { }} // No unsaved changes in read-only mode
            globalAccordionAction={null}
            readOnly={true} // Make all fields read-only
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ pt: 3 }}>
        <Buttons
          advance={
            <Button
              onClick={onClose}
              variant="contained"
              title={`Close ${title.toLowerCase()}`}
            >
              Close
            </Button>
          }
        />
      </DialogActions>
    </Dialog>
  );
};

export default ResponseModal;